import { getScopeEntitiesRequest, parseContractComplianceError } from 'api/requests/contract-compliance-requests';
import { UPDropdownItem } from 'components/dropdown/types';
import { ScopeLevel } from 'constants/ScopeLevel';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR } from 'translations/constants';
import { IScopeEntity } from 'types/contract-compliance/IContractCompliance';
import { getContractComplianceErrorMessage } from '../utils';

export type ScopeEntitiesStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Dropdown options per scope level, loaded once per level and kept while the page is open. */
export const useScopeEntities = () => {
  const [t] = useTranslation();
  const cache = useRef<Partial<Record<ScopeLevel, IScopeEntity[]>>>({});
  const [statusByLevel, setStatusByLevel] = useState<Partial<Record<ScopeLevel, ScopeEntitiesStatus>>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const load = useCallback(
    async (level: ScopeLevel, force = false) => {
      // Agency has no entities to pick.
      if (level === ScopeLevel.AGENCY) return;
      if (cache.current[level] && !force) return;
      setStatusByLevel(previous => ({ ...previous, [level]: 'loading' }));
      setErrorMessage(null);
      try {
        cache.current[level] = await getScopeEntitiesRequest(level);
        setStatusByLevel(previous => ({ ...previous, [level]: 'ready' }));
      } catch (error) {
        setStatusByLevel(previous => ({ ...previous, [level]: 'error' }));
        setErrorMessage(
          getContractComplianceErrorMessage(
            t,
            parseContractComplianceError(error),
            CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR,
          ),
        );
      }
    },
    [t],
  );

  const getEntities = useCallback((level: ScopeLevel): IScopeEntity[] => cache.current[level] ?? [], []);

  const toDropdownItems = useCallback(
    (level: ScopeLevel): UPDropdownItem[] =>
      getEntities(level).map(entity => ({ label: entity.name, value: String(entity.id) })),
    // statusByLevel: the cache is a ref, so re-derive when a level finishes loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getEntities, statusByLevel],
  );

  /** Names for error messages; falls back to the id for an entity no longer in the list. */
  const getEntityNames = useCallback(
    (level: ScopeLevel, ids: number[]): string[] =>
      ids.map(id => getEntities(level).find(entity => entity.id === id)?.name ?? String(id)),
    [getEntities],
  );

  return { load, getEntities, toDropdownItems, getEntityNames, statusByLevel, errorMessage };
};

export type UseScopeEntitiesResult = ReturnType<typeof useScopeEntities>;
